import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { fetchEventBlogs } from '../../services/calendar';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import ErrorDisplay from '../error-display/error-display';
import './event-calendar.css';

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Transform blog posts into calendar events
  const transformBlogsToEvents = useCallback((eventBlogs) => {
    return eventBlogs.map(blog => {
      // Create event start date from Firestore timestamp
      const eventStart = blog.eventDate.toDate();
      
      // Create event object
      const eventObj = {
        id: blog.id,
        title: blog.title,
        start: eventStart,
        allDay: false,
        extendedProps: {
          blogId: blog.id,
          imageUrl: blog.imageUrl,
          description: blog.content
        }
      };
      
      // Add end date if available
      if (blog.eventEndDate) {
        eventObj.end = blog.eventEndDate.toDate();
      }
      
      return eventObj;
    });
  }, []);

  // Load events
  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const eventBlogs = await fetchEventBlogs();
        const calendarEvents = transformBlogsToEvents(eventBlogs);
        setEvents(calendarEvents);
      } catch (err) {
        setError('Error loading events: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadEvents();
  }, [transformBlogsToEvents]);

  const handleEventClick = (clickInfo) => {
    // Navigate to the blog post with a flag indicating we came from the calendar
    navigate(`/blogs/${clickInfo.event.id}?from=calendar`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <div className="event-calendar">
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay'
        }}
        events={events}
        eventClick={handleEventClick}
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          meridiem: 'short'
        }}
        height="auto"
        eventDisplay="block"
        // Using the site's red theme color
        eventBackgroundColor="rgb(203, 55, 51)"
        eventBorderColor="rgb(203, 55, 51)"
        eventTextColor="#ffffff"
      />
    </div>
  );
};

export default EventCalendar;